import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';

import {
	companyPropertiesConstants,
	companyPropertiesExtendedConstants,
	companyPropertyKeys,
} from '../constants/appConstants';
import CompanyRepository from '../repositories/companyRepository';
import { queryClient } from '../router/Root';
import type { CompanyState, KeyValue } from '../zustandStore/slices';

import { userKeys } from './query-keys';
import { userQuery } from './userQuery';

export const companyKeys = {
	getCompanyUsers: 'getCompanyUsers',
	getFeatures: () => ['company', 'features', 'all'] as const,
	getProperties: ['company', 'properties', 'all'] as const,
	getRequestLimit: ['company', 'properties', 'requestLimit'] as const,
	getWhitelistedDomains: ['company', 'properties', 'whitelistedDomains'] as const,
};

export const useSaveCompanyNameQuery = () => {
	return useMutation<void, unknown, { companyName: string }>({
		mutationFn: CompanyRepository.saveCompanyName,
		onSettled: () => queryClient.invalidateQueries({ queryKey: [userKeys.getUser] }),
	});
};

export const companyFeaturesQuery = queryOptions({
	queryKey: companyKeys.getFeatures(),
	queryFn: CompanyRepository.getCompanyFeatures,
});

export const useGetCompanyFeaturesQuery = () => {
	return useQuery(companyFeaturesQuery);
};

export const useSaveCompanyFeatureQuery = () => {
	return useMutation({ mutationFn: CompanyRepository.saveCompanyFeature });
};

const getCompanyPropertyWhitelistedDomainsQuery = (companyId: string) => {
	return {
		queryKey: [companyKeys.getWhitelistedDomains, companyId],
		queryFn: (): Promise<string[]> => CompanyRepository.getCompanyPropertyWhitelistedDomains({ companyId }),
	};
};

export const useGetCompanyPropertyWhitelistedDomainsQuery = (companyId: string) => {
	const queryInfo = getCompanyPropertyWhitelistedDomainsQuery(companyId);

	return useQuery<string[]>({
		...queryInfo,
		enabled: Boolean(companyId),
	});
};

export type SaveCompanyMutationType = {
	companyId: CompanyState['company']['companyId'];
	key: KeyValue['key'];
	value: KeyValue['value'];
};

const saveCompanyProperty = async ({ companyId, key, value }: SaveCompanyMutationType) => {
	if (key === companyPropertiesExtendedConstants.removeWhiteListedDomain) {
		return await CompanyRepository.deleteCompanyPropertyWhitelistedDomain({
			companyId,
			domain: value,
		});
	} else if (key === companyPropertiesExtendedConstants.addWhiteListedDomain) {
		await CompanyRepository.saveCompanyPropertyWhitelistedDomain({
			companyId,
			domain: value,
		});
	} else {
		await CompanyRepository.saveCompanyProperty({
			companyId,
			key,
			value,
		});
	}
};

export const useSaveCompanyPropertyQuery = () => {
	return useMutation<void, unknown, SaveCompanyMutationType>({ mutationFn: saveCompanyProperty });
};

export const getRequestLimitQuery = () => {
	const propertyKey = companyPropertyKeys.requestLimit;
	return {
		queryKey: [companyKeys.getRequestLimit, propertyKey],
		queryFn: async () => {
			const userData = await queryClient.ensureQueryData(userQuery);

			return CompanyRepository.getCompanyProperty({
				companyAccountId: userData?.companyAccountId,
				propertyKey,
			});
		},
	};
};

export const useGetRequestLimitQuery = () => {
	return useQuery(getRequestLimitQuery());
};

export const companyPropertiesQuery = (
	companyId: string | undefined,
	properties: Partial<Pick<typeof companyPropertiesConstants, keyof typeof companyPropertiesConstants>>,
) =>
	queryOptions({
		queryKey: [companyKeys.getProperties, companyId, properties],
		queryFn: () => getCompanyProperties(companyId || '', properties),
		enabled: Boolean(companyId),
	});

export type CompanyPropertiesResponse = {
	whiteListedDomains: string[];
	defaultExpirationDate: number;
	companyApprovalLimit: number;
	virtualCardExpRange: number;
	requestLimit: number;
	showactivity: any;
};

async function getCompanyProperties(
	companyId: string,
	properties: Partial<Pick<typeof companyPropertiesConstants, keyof typeof companyPropertiesConstants>>,
): Promise<CompanyPropertiesResponse> {
	const promises = Object.entries(companyPropertiesConstants).map(async ([propertyKey, keyValue]) => {
		const promise =
			keyValue === properties['whiteListedDomains']
				? CompanyRepository.getCompanyPropertyWhitelistedDomains({ companyId })
				: CompanyRepository.getCompanyProperty({ companyAccountId: companyId, propertyKey: keyValue });

		return promise.then(value => {
			return { propertyKey, value };
		});
	});

	const results = await Promise.allSettled(promises);
	const filteredResults = results.filter(result => result.status === 'fulfilled');

	return filteredResults.reduce((acc, result: any) => {
		acc[result.value.propertyKey] =
			result.value.propertyKey === 'defaultExpirationDate' && typeof result.value.value === 'object'
				? ''
				: result.value.value;
		return acc;
	}, {} as CompanyPropertiesResponse);
}
